import { Fragment, useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Avatar from 'react-avatar';
import useFetch from '../../../hooks/use-fetch';
import {uploadUrl} from '../../../config/public-config';

import {CurrentUserContext} from '../../../contexts/current-user';

import { Dialog, Transition } from '@headlessui/react';
import Form, { Field } from '@atlaskit/form';
import TextField from '@atlaskit/textfield';
import Select from '@atlaskit/select';

import ErrorsBlock from '../../../components/errors';


export default function FormOrganization({itemId, closeModal, isOpen, reloadTable, tableData}) {
  const { t } = useTranslation();
  const [currentUserState, setCurrentUserState] = useContext(CurrentUserContext);
  const [formData, setFormData] = useState('');
  const [editItem, setEditItem] = useState('');
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [timezone, setTimezone] = useState('');
  const [imageId, setImageId] = useState('');
  const [errors, setErrors] = useState([]);

  const isNewItem = itemId === 'new';

  const [{response: userResponse}, doFetchUser] = useFetch('/users/account', true);
  const [{response: itemUpdateResponse, isLoading: itemUpdateIsLoading, error: itemUpdateError}, doFetchItemUpdate] = useFetch(`/users/sites/${itemId}`, true);
  const [{response: itemSaveResponse, isLoading: itemSaveIsLoading, error: itemSaveError}, doFetchItemSave] = useFetch(`/users/sites`, true);
  const [{response: imageUploadResponse, isLoading: imageUploadIsLoading, error: imageUploadError}, doFetchImageUpload] = useFetch(`/uploads`, true);

  useEffect(() => {
    setFormData(new FormData());
    if (itemId === "new") {
      setName('');
      setShortName('');
      setSubdomain('');
      setTimezone('');
      setImageId('');
      return;
    }

    const organization = tableData.find(item => item.id === itemId);
    setName(organization.attributes.name);
    setShortName(organization.attributes.short_name);
    setSubdomain(organization.attributes.subdomain);
    setImageId(organization.attributes.image.id ? organization.attributes.image.id : '');
  }, [itemId]);

  useEffect(() => {
    if (!imageUploadResponse) {
      return;
    }
    doFetchItemUpdate({
      method: 'PUT',
      data: {
        data: {
          image: { ...imageUploadResponse.data },
        }
      }
    });
    setImageId(imageUploadResponse.data.id)
  }, [imageUploadResponse]);

  useEffect(() => {
    if (!userResponse) {
      return;
    }
    setCurrentUserState(state => ({
      ...state,
      sites: userResponse.data.attributes.sites,
      currentSite: userResponse.data.attributes.sites.find(item => item.id === state.currentSite.id)
    }));
  }, [userResponse]);

  useEffect(() => {
    if (!itemSaveResponse) {
      return;
    }
    doFetchUser();
    closeModal();
    reloadTable();
  }, [itemSaveResponse]);

  useEffect(() => {
    if (!itemUpdateResponse) {
      return;
    }
    doFetchUser();
    closeModal();
    reloadTable();
  }, [itemUpdateResponse]);

  const onFileChoice = (value) => {
    formData.set('attachment', value);
    doFetchImageUpload({
      method: 'POST',
      data: formData
    });
  };

  const onSaveClick = (data) => {
    if (isNewItem) {
      doFetchItemSave({
        method: 'POST',
        data: {
          data: data
        }
      });
    } else {
      doFetchItemUpdate({
        method: 'PUT',
        data: {
          data: data
        }
      });
    }
  };

  useEffect(() => {
    if (itemSaveError) {
      setErrors(itemSaveError.errors)
    }
  }, [itemSaveError]);

  useEffect(() => {
    if (itemUpdateError) {
      setErrors(itemUpdateError.errors)
    }
  }, [itemUpdateError]);

  useEffect(() => {
    if(!isOpen) {
      setTimeout(() => {
        setErrors([])
      }, 300);
    }
  },[isOpen])


  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={closeModal}
      >
        <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
        <div className="min-h-screen px-4 text-center">
          {/* <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span> */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="inline-block w-full max-w-md p-6 mt-32 my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded">
              <Dialog.Title
                as="h3"
                className="text-lg font-medium leading-6 text-gray-900"
              >
                {isNewItem ? t("adding_organization") : t("editing_organization")}
              </Dialog.Title>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  {t("organization_form_text")}
                </p>
              </div>
              {errors.length > 0 ? <ErrorsBlock errors={errors}/> : ''}
              <div className="mt-4">
                <div className="bg-white flex">
                  <div className="mb-4 w-32 h-32">
                    {imageId === '' ? <Avatar name={name} /> : (
                      <img src={uploadUrl(imageId)} alt="" className="w-auto mx-auto rounded-lg object-cover object-center"/>
                    )}
                  </div>
                  <div className="ml-10 flex flex-col">
                    <span className="text-gray-800 text-xs font-light mb-4">{t('avatar_upload_text')}</span>
                    <label className="cursor-pointer">
                      <span className="mt-2 leading-normal px-4 py-2 text-blue-900 bg-blue-100 text-xs rounded-full" >{t('avatar_upload')}</span>
                      <input
                        type="file"
                        className="hidden"
                        onChange={(evt) => onFileChoice(evt.target.files[0])}
                      />
                    </label>
                    
                  </div>
                </div>
              </div>
              <Form onSubmit={data => onSaveClick(data)}>
                {({ formProps }) => (
                  <form {...formProps} className="text-sm">
                    <Field name="name" defaultValue={name || ""} label={t('title_name')} isRequired>
                      {({ fieldProps }) => <TextField {...fieldProps} />}
                    </Field>
                    <Field name="short_name" defaultValue={shortName || ""} label={t('abbreviation')}>
                      {({ fieldProps }) => <TextField {...fieldProps}/>}
                    </Field>
                    {/* <Field name="subdomain" defaultValue={subdomain} label="Поддомен:">
                      {({ fieldProps }) => <TextField {...fieldProps}/>}
                    </Field> */}
                    <div className="flex justify-between mt-8">
                      <button 
                        type="submit" 
                        className="inline-flex justify-center px-4 py-2 text-sm font-medium text-blue-900 bg-blue-100 border border-transparent rounded hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                      >
                        {t('save')}
                      </button>
                      <button 
                        onClick={() => closeModal()}
                        type="button" 
                        className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-900 bg-gray-100 border border-transparent rounded hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                      >
                        {t('cancel')}
                      </button>
                    </div>
                  </form>
                )}
              </Form>
            </div>
          </Transition.Child>
        </div>
        
      </Dialog>
    </Transition>
  )
}
